import React, {useState} from 'react'
import moment from 'moment'
import {Badge, Colors, IconButton, Modal, P} from '../../../../components/UI/index.js'

export const TYPES = [
    {value: 'error', title: 'Fouten'},
    {value: 'webhook', title: 'Webhooks'},
    {value: 'webshop', title: 'Webshop'},
    {value: 'httpsRequest', title: 'Uitgaande requests'},
    {value: 'apiRequest', title: 'API requests'}
]

// Of the request itself (apiRequest, httpsRequest, webhook) or of the failed request of an error
export function requestOf(log) {
    return {
        method: log.method || log.meta?.requestOptions?.method || '',
        url: log.host || log.path ? `${log.host || ''}${log.path || ''}${log.query ? `?${log.query}` : ''}` : `${log.meta?.requestOptions?.host || ''}${log.meta?.requestOptions?.path || ''}`
    }
}

export function messageOf(log) {
    if (log.err?.message) {
        return log.err.message
    }

    if (log.message) {
        return log.message
    }

    const {method, url} = requestOf(log)

    return log.statusCode ? `${method} ${url}: ${log.statusCode}` : ''
}

export function durationOf(log) {
    const ms = log.responseTime ?? log.meta?.takenTimeMs
    return ms ? `${(ms / 1000).toFixed(2).replace('.', ',')}s` : ''
}

const monospace = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

const sectionTitleStyle = {fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: Colors.textMedium, marginBottom: 6}

const codeStyle = {
    margin: 0,
    padding: 12,
    background: Colors.grey20,
    borderRadius: 4,
    fontFamily: monospace,
    fontSize: 12,
    lineHeight: 1.5,
    color: Colors.textExtraDark,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: 320,
    overflow: 'auto'
}

// A JSON string (a body or headers) indented, other text as it is
function pretty(value) {
    if (typeof value !== 'string') {
        return JSON.stringify(value, null, 2)
    }

    try {
        return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
        return value
    }
}

function copy(text) {
    navigator.clipboard?.writeText(text).catch(() => {})
}

function Section({title, children}) {
    return (
        <div style={{marginTop: 20}}>
            <div style={sectionTitleStyle}>{title}</div>
            {children}
        </div>
    )
}

// The copy button is placed by a wrapper: IconButton's style goes to its <button>, inside an element of its own. It
// stays clear of the scrollbar, and the text never goes under it
function Code({children, copyable}) {
    return (
        <div style={{position: 'relative'}}>
            <pre style={{...codeStyle, paddingRight: copyable ? 52 : codeStyle.padding}}>{children}</pre>

            {copyable &&
                <div style={{position: 'absolute', top: 4, right: 16}}>
                    <IconButton
                        icon='mdi mdi-content-copy'
                        tooltip='Kopiëren'
                        onClick={() => copy(children)}
                    />
                </div>
            }
        </div>
    )
}

// Collapsed by default: headers are rarely needed
function Collapsible({title, children}) {
    const [open, setOpen] = useState(false)

    return (
        <div style={{marginTop: 20}}>
            <div
                style={{...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', userSelect: 'none', marginBottom: open ? 6 : 0}}
                onClick={() => setOpen(!open)}
            >
                <i className={`mdi ${open ? 'mdi-chevron-down' : 'mdi-chevron-right'}`} style={{fontSize: 16}} />
                {title}
            </div>

            {open && children}
        </div>
    )
}

// title instead of the value, e.g. a name with the id in value (which is copied)
function Field({label, value, title, onFilter}) {
    return (
        <div style={{minWidth: 0}}>
            <div style={{...sectionTitleStyle, marginBottom: 2}}>{label}</div>

            <div style={{display: 'flex', alignItems: 'center', gap: 2, minWidth: 0}}>
                <P ellipsis style={{fontSize: 13}}>
                    {title && <span>{title} </span>}
                    <span style={{fontFamily: monospace, color: title ? Colors.textMedium : undefined}}>{title ? `(${value})` : value}</span>
                </P>
                <IconButton icon='mdi mdi-content-copy' tooltip='Kopiëren' onClick={() => copy(value)} />
                {onFilter && <IconButton icon='mdi mdi-filter' tooltip='Filter op deze waarde' onClick={onFilter} />}
            </div>
        </div>
    )
}

/**
 * Everything of one log entry: what happened, for whom, the request, the stack trace and the full entry. onFilter
 * filters the logs on an id of it (and closes the details)
 */
export default function LogDetails({log, resellers, customerNames, onClose, onFilter}) {
    if (!log) {
        return <Modal show={false} />
    }

    const type = TYPES.find((type) => type.value === log.type)
    const status = log.statusCode || log.err?.statusCode
    const {method, url} = requestOf(log)
    const duration = durationOf(log)
    const reseller = resellers.find((reseller) => reseller._id === log.resellerId)?.name
    const filter = (filters) => onFilter && (() => onFilter(filters))

    const ids = [
        {label: 'Reseller', value: log.resellerId, title: reseller, onFilter: filter({resellerId: log.resellerId})},
        {label: 'Klant', value: log.customerId, title: customerNames.get(log.customerId)?.name, onFilter: filter({customerId: log.customerId})},
        {label: 'User', value: log.userId},
        {label: 'Admin', value: log.adminId},
        {label: 'Ingelogd als klant door', value: log.impersonatedBy},
        {label: 'Rit', value: log.orderId, onFilter: filter({id: log.orderId})},
        {label: 'Zending', value: log.parcelId, onFilter: filter({id: log.parcelId})},
        {label: 'Dienst', value: log.shiftId, onFilter: filter({id: log.shiftId})},
        {label: 'Route', value: log.routeId, onFilter: filter({id: log.routeId})},
        {label: 'Factuur', value: log.invoiceId, onFilter: filter({id: log.invoiceId})},
        {label: 'Abonnement', value: log.subscriptionId, onFilter: filter({id: log.subscriptionId})},
        {label: 'Correlation ID', value: log.correlationId, onFilter: filter({correlationId: log.correlationId})},
        {label: 'Origin', value: log.origin || log.meta?.origin}
    ].filter((field) => field.value)

    const stack = log.err?.stack || log.meta?.componentStack

    return (
        <Modal
            show
            closeButton
            closeOnBackdropClick
            onClose={onClose}
            style={{maxWidth: 960, padding: 24, background: Colors.backgroundWhite}}
            title={log.err?.name || log.action || log.subject || type?.title || 'Log'}
        >
            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: -12}}>
                {type && <Badge size='sm'>{type.title}</Badge>}
                {status && <Badge size='sm' variant={status >= 400 ? 'error' : 'success'}>{status}</Badge>}
                {log.retries > 0 && <Badge size='sm' variant='warning'>{`${log.retries} keer opnieuw`}</Badge>}
                <P style={{color: Colors.textMedium, fontSize: 13}}>
                    {moment(log.timestamp).format('DD-MM-YYYY HH:mm:ss')}{duration ? ` · ${duration}` : ''}
                </P>
            </div>

            {messageOf(log) &&
                <P style={{marginTop: 16, fontSize: 15, color: Colors.textExtraDark, wordBreak: 'break-word'}}>{messageOf(log)}</P>
            }

            {url &&
                <Section title='Request'>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: Colors.grey20, borderRadius: 4, minWidth: 0}}>
                        {method && <span style={{fontFamily: monospace, fontSize: 12, fontWeight: 700, color: Colors.infoDark}}>{method}</span>}
                        <span style={{fontFamily: monospace, fontSize: 12, color: Colors.textExtraDark, wordBreak: 'break-all', flex: 1}}>{url}</span>
                        <IconButton icon='mdi mdi-content-copy' tooltip='Kopiëren' onClick={() => copy(url)} />
                    </div>
                </Section>
            }

            {ids.length > 0 &&
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px 24px', marginTop: 20}}>
                    {ids.map((field) => <Field key={field.label} {...field} />)}
                </div>
            }

            {stack &&
                <Section title='Stack trace'>
                    <Code copyable>{stack}</Code>
                </Section>
            }

            {log.body &&
                <Section title='Request body'>
                    <Code copyable>{pretty(log.body)}</Code>
                </Section>
            }

            {log.responseBody &&
                <Section title='Response body'>
                    <Code copyable>{pretty(log.responseBody)}</Code>
                </Section>
            }

            {log.headers &&
                <Collapsible title='Request headers'>
                    <Code>{pretty(log.headers)}</Code>
                </Collapsible>
            }

            {log.responseHeaders &&
                <Collapsible title='Response headers'>
                    <Code>{pretty(log.responseHeaders)}</Code>
                </Collapsible>
            }

            <Section title='JSON'>
                <Code copyable>{JSON.stringify(log, null, 2)}</Code>
            </Section>
        </Modal>
    )
}
